import React, { useState, useCallback } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Alert,
} from '@amzn/awsui-components-console';
import { Schedule } from '../../types';

/**
 * Props for the DeleteConfirmModal component
 */
export interface DeleteConfirmModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Callback when the modal is dismissed (Cancel clicked or modal closed) */
  onDismiss: () => void;
  /** Callback when deletion is confirmed - should call the delete API */
  onConfirm: () => Promise<void>;
  /** The schedule to be deleted */
  schedule: Schedule | null;
}

/**
 * DeleteConfirmModal Component
 * 
 * A confirmation modal for deleting schedules. This component:
 * - Displays the schedule name being deleted
 * - Shows a permanent deletion warning
 * - Has Cancel and Delete buttons
 * - Shows loading state during deletion
 * - Handles errors appropriately
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4
 * - 4.1: Display a confirmation dialog when user clicks Delete
 * - 4.2: Clearly state the schedule name and warn that deletion is permanent
 * - 4.3: Call DeleteSchedule API and remove schedule from list (via onConfirm)
 * - 4.4: Display error notification and keep schedule in list on API error
 * 
 * @example
 * ```tsx
 * <DeleteConfirmModal
 *   visible={showDeleteModal}
 *   onDismiss={() => setShowDeleteModal(false)}
 *   onConfirm={async () => {
 *     await deleteSchedule(scheduleToDelete.scheduleId);
 *   }}
 *   schedule={scheduleToDelete}
 * />
 * ```
 */
const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  visible,
  onDismiss,
  onConfirm,
  schedule,
}) => {
  // Loading state during deletion
  const [isDeleting, setIsDeleting] = useState(false);
  // Error state for displaying API errors
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle the delete confirmation
   * Calls the onConfirm callback and handles loading/error states
   */
  const handleConfirm = useCallback(async () => {
    setError(null);
    setIsDeleting(true);

    try {
      await onConfirm();
    } catch (err: any) {
      // Keep modal open and display error - Requirement 4.4
      console.error('Failed to delete schedule:', err);
      setError(err.message || 'Failed to delete schedule. Please try again.');
      setIsDeleting(false);
      return;
    }

    setIsDeleting(false);
    // Success - close modal (parent handles success notification)
    onDismiss();
  }, [onConfirm, onDismiss]);

  /**
   * Handle modal dismiss
   * Prevents dismissal while deletion is in progress
   */
  const handleDismiss = useCallback(() => {
    if (!isDeleting) {
      setError(null);
      onDismiss();
    }
  }, [isDeleting, onDismiss]);

  // Don't render if no schedule is provided
  if (!schedule) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeAriaLabel="Close delete confirmation"
      size="medium"
      header="Delete Schedule"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={handleDismiss}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={isDeleting}
              data-testid="confirm-delete-button"
            >
              Delete
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {/* Error Display - Requirement 4.4 */}
        {error && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setError(null)}
            header="Error"
          >
            {error}
          </Alert>
        )}

        {/* Permanent Deletion Warning - Requirement 4.2 */}
        <Alert type="warning" header="This action cannot be undone">
          Deleting a schedule is permanent. All schedule configuration and 
          execution history associated with this schedule will be lost.
        </Alert>

        {/* Schedule Name Display - Requirement 4.2 */}
        <Box>
          <Box variant="p">
            Are you sure you want to delete the following schedule?
          </Box>
          <Box variant="h4" margin={{ top: 's' }}>
            {schedule.scheduleName}
          </Box>
          {schedule.description && (
            <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
              {schedule.description}
            </Box>
          )}
        </Box>
      </SpaceBetween>
    </Modal>
  );
};

export default DeleteConfirmModal;
